/**
 * Entry point ACORDEI! — versão pós-fusão (ADR 0008) + 100% REST (ADR 0009 + cleanup).
 *
 * Original Manus inicializava trpc.Provider + httpBatchLink. Como o backend
 * TypeScript foi descartado em favor do FastAPI Python, todo o pipeline de
 * dados passou pra `lib/api.ts` (REST). Aqui só inicializa o React Query
 * pra cache de chamadas REST + renderiza o App.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { installAdminFetchPatch } from "./lib/admin-auth";
import { installUsuarioFetchPatch } from "./lib/usuario-auth";
import { reportClientError, setupGlobalErrorReporting } from "./lib/errorReporter";
import { initSentryFrontend } from "./lib/sentryInit";
import { inicializarInstallPrompt, registrarServiceWorker } from "./lib/pwa";
import { inicializarI18n } from "./lib/i18n";

// PR #83: inicia Sentry ANTES de qualquer outro handler. Em dev sem env var,
// é no-op. Em prod, captura erros + tracing.
void initSentryFrontend();

// PR #90: register service worker + capture beforeinstallprompt event.
// Em dev (sem HTTPS + bundle correto), SW pode falhar — não bloqueia app.
void registrarServiceWorker();
inicializarInstallPrompt();

// PR #106: seta <html lang="pt|en"> conforme idioma persistido/detectado.
inicializarI18n();

// PR #43: registra handlers globais (window.onerror, unhandledrejection)
// antes de qualquer render. Tudo que escapar boundary cai aqui.
setupGlobalErrorReporting();

// PR #279: monkey-patch global fetch pra injetar Authorization em /api/v1/admin/*
// quando token estático estiver salvo. Zero mudança nas páginas admin.
installAdminFetchPatch();
// Injeta o Bearer do usuário/contador em /api/v1/usuario/* (login por senha).
installUsuarioFetchPatch();

// PR #90: registra service worker em produção + captura beforeinstallprompt
inicializarInstallPrompt();
void registrarServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    const err = event.query.state.error;
    console.error("[API Query Error]", err);
    // Reporta erros de query — útil pra ver 5xx em prod sem precisar do user reportar
    if (err instanceof Error) {
      void reportClientError({
        origem: "manual",
        mensagem: `[query] ${err.message}`,
        stack: err.stack,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        payload: { queryKey: event.query.queryKey },
      });
    }
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    const err = event.mutation.state.error;
    console.error("[API Mutation Error]", err);
    if (err instanceof Error) {
      void reportClientError({
        origem: "manual",
        mensagem: `[mutation] ${err.message}`,
        stack: err.stack,
        url: typeof window !== "undefined" ? window.location.href : undefined,
      });
    }
  }
});

createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);
